import {
  HStack,
  Icon,
  IconButton,
  PercentageInput,
  SearchSelect,
} from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Change, FormOption } from '../../../../../../../../types/Form';
import useFlowInputComponent from '../../../../../hooks/useFlowsInputComponent';
import { useFlowsProgression } from '../../../../../hooks/useFlowsProgression';
import { FlowsQuestion } from '../../../../../types/FlowsTypes';
import useAvailableOptions from '../hooks/useAvailableOptions';
import useNaceCodeOptions from '../hooks/useNaceCodeOptions';

interface Props {
  question: FlowsQuestion;
  keyName?: string;
  value?: number;
  disabled?: boolean;
}

export interface KVPercentage {
  key: string;
  value: number;
}

const SingleIndexPercentageInput: React.FC<Props> = ({
  question,
  keyName,
  value: percentageValue,
  disabled,
}) => {
  const { t } = useTranslation();
  const {
    field: { value, onBlur, onChange },
    error,
    prefilled,
  } = useFlowInputComponent({ question });
  const { saveSingleField } = useFlowsProgression();

  const enumOptions = useNaceCodeOptions(question);
  const chosenValues = (value || []) as KVPercentage[];
  const currentIndex = chosenValues.findIndex(
    ({ key: _key }) => _key === keyName,
  );

  const availableOptions = useAvailableOptions(
    question,
    enumOptions,
    chosenValues,
  );
  const availableOptionWithCurrent = [
    ...availableOptions,
    enumOptions.find(({ value }) => value === keyName),
  ].filter((item) => !!item) as FormOption[];

  const handleSelectChange = (key: string | null) => {
    const newList = [...chosenValues];
    newList.splice(currentIndex, 1, {
      key: key || '',
      value: percentageValue!,
    });
    onBlur({
      name: question.id,
      value: newList,
    });
  };

  const handlePercentageChange = (e: Change) => {
    const newList = [...chosenValues];
    newList.splice(currentIndex, 1, {
      key: keyName!,
      value: e.value as number,
    });
    onChange({
      name: question.id,
      value: newList,
    });
  };

  const handlePercentageBlur = () => {
    onBlur({
      name: question.id,
      value: chosenValues.map((item) => ({
        ...item,
        value: (item.value as unknown as string) === '' ? 0 : item.value,
      })),
    });
  };

  const handleRemove = () => {
    const newList = [...chosenValues];
    newList.splice(currentIndex, 1);
    onBlur({
      name: question.id,
      value: newList,
    });
    // Debounce not working
    saveSingleField(question.id);
  };

  return (
    <HStack spacing="4">
      <SearchSelect
        placeholder={t('DEFAULT_SELECT_PLACEHOLDER')}
        onChange={handleSelectChange}
        data={availableOptionWithCurrent}
        error={error?.message}
        value={keyName}
        prefilled={prefilled}
        data-test-id={`FLOWS_SELECT-INPUT_${question.id
          .toUpperCase()
          .replaceAll('.', '-')}`}
        // Legacy styling to match flows inputs
        extraStyles={{
          input: {
            height: '45px',
            boxShadow:
              '0 4px 8px rgba(0,0,0,0.04),0 0 2px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.04)',
            borderColor: prefilled ? '#FFBC0F' : 'rgb(228, 228, 228)',
          },
          dropdown: {
            borderColor: 'rgb(228, 228, 228)',
          },
        }}
        flexGrow={1}
        isDisabled={disabled}
      />
      <PercentageInput
        name={question.id}
        value={percentageValue?.toString()}
        onChange={handlePercentageChange}
        onBlur={handlePercentageBlur}
        variant="flows"
        minW="32"
        isDisabled={disabled}
      />
      <IconButton
        icon={<Icon name="GeneralActionsCloseOrRemove" />}
        aria-label="Remove entry"
        onClick={handleRemove}
        variant="ghost"
        isDisabled={disabled}
      />
    </HStack>
  );
};

export default SingleIndexPercentageInput;
